import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

export default function MerchantManagement() {
  const navigate = useNavigate();
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedMerchant, setSelectedMerchant] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    merchantCode: '',
    description: '',
    logoUrl: '',
    websiteUrl: '',
    contactEmail: '',
    contactPhone: '',
    receivingAccountNumber: '',
    webhookUrl: '',
    isActive: true,
    allowTransfers: true,
    allowPayments: true,
    customFeePercentage: '',
    customFlatFee: '',
    minTransactionAmount: '',
    maxTransactionAmount: '',
    dailyTransactionLimit: ''
  });

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (!user.isAdmin) {
      navigate('/login');
      return;
    }
    fetchMerchants();
  }, [navigate]);

  const fetchMerchants = async () => {
    try {
      setLoading(true);
      const response = await api.get('/Merchant');
      setMerchants(response.data);
    } catch (err) {
      setError('Failed to fetch merchants');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      merchantCode: '',
      description: '',
      logoUrl: '',
      websiteUrl: '',
      contactEmail: '',
      contactPhone: '',
      receivingAccountNumber: '',
      webhookUrl: '',
      isActive: true,
      allowTransfers: true,
      allowPayments: true,
      customFeePercentage: '',
      customFlatFee: '',
      minTransactionAmount: '',
      maxTransactionAmount: '',
      dailyTransactionLimit: ''
    });
  };

  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const openEditModal = (merchant) => {
    setSelectedMerchant(merchant);
    setFormData({
      name: merchant.name || '',
      merchantCode: merchant.merchantCode || '',
      description: merchant.description || '',
      logoUrl: merchant.logoUrl || '',
      websiteUrl: merchant.websiteUrl || '',
      contactEmail: merchant.contactEmail || '',
      contactPhone: merchant.contactPhone || '',
      receivingAccountNumber: merchant.receivingAccountNumber || '',
      webhookUrl: merchant.webhookUrl || '',
      isActive: merchant.isActive,
      allowTransfers: merchant.allowTransfers,
      allowPayments: merchant.allowPayments,
      customFeePercentage: merchant.customFeePercentage || '',
      customFlatFee: merchant.customFlatFee || '',
      minTransactionAmount: merchant.minTransactionAmount || '',
      maxTransactionAmount: merchant.maxTransactionAmount || '',
      dailyTransactionLimit: merchant.dailyTransactionLimit || ''
    });
    setShowEditModal(true);
  };

  const openViewModal = async (merchant) => {
    try {
      const response = await api.get(`/Merchant/${merchant.id}`);
      setSelectedMerchant(response.data);
      setShowViewModal(true);
    } catch (err) {
      setError('Failed to fetch merchant details');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        customFeePercentage: formData.customFeePercentage ? parseFloat(formData.customFeePercentage) : null,
        customFlatFee: formData.customFlatFee ? parseFloat(formData.customFlatFee) : null,
        minTransactionAmount: formData.minTransactionAmount ? parseFloat(formData.minTransactionAmount) : null,
        maxTransactionAmount: formData.maxTransactionAmount ? parseFloat(formData.maxTransactionAmount) : null,
        dailyTransactionLimit: formData.dailyTransactionLimit ? parseFloat(formData.dailyTransactionLimit) : null
      };

      await api.post('/Merchant', payload);
      setSuccess('Merchant created successfully!');
      setShowCreateModal(false);
      resetForm();
      fetchMerchants();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create merchant');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        customFeePercentage: formData.customFeePercentage ? parseFloat(formData.customFeePercentage) : null,
        customFlatFee: formData.customFlatFee ? parseFloat(formData.customFlatFee) : null,
        minTransactionAmount: formData.minTransactionAmount ? parseFloat(formData.minTransactionAmount) : null,
        maxTransactionAmount: formData.maxTransactionAmount ? parseFloat(formData.maxTransactionAmount) : null,
        dailyTransactionLimit: formData.dailyTransactionLimit ? parseFloat(formData.dailyTransactionLimit) : null
      };

      await api.put(`/Merchant/${selectedMerchant.id}`, payload);
      setSuccess('Merchant updated successfully!');
      setShowEditModal(false);
      fetchMerchants();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update merchant');
    }
  };

  const handleToggleStatus = async (merchant) => {
    try {
      await api.post(`/Merchant/${merchant.id}/toggle-status`);
      setSuccess(`Merchant ${merchant.isActive ? 'deactivated' : 'activated'} successfully!`);
      fetchMerchants();
    } catch (err) {
      setError('Failed to toggle merchant status');
    }
  };

  const handleDelete = async (merchant) => {
    if (!window.confirm(`Are you sure you want to delete "${merchant.name}"?`)) return;

    try {
      await api.delete(`/Merchant/${merchant.id}`);
      setSuccess('Merchant deleted successfully!');
      fetchMerchants();
    } catch (err) {
      setError('Failed to delete merchant');
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₦0';
    return '₦' + parseFloat(amount).toLocaleString();
  };

  if (loading) {
    return <div style={styles.loading}>Loading merchants...</div>;
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1>🏪 Merchant Management</h1>
        <div style={styles.headerButtons}>
          <button onClick={() => navigate('/admin')} style={styles.backButton}>
            ← Back to Admin
          </button>
          <button onClick={openCreateModal} style={styles.createButton}>
            + Add Merchant
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div style={styles.errorAlert}>
          {error}
          <button onClick={() => setError('')} style={styles.closeAlert}>×</button>
        </div>
      )}
      {success && (
        <div style={styles.successAlert}>
          {success}
          <button onClick={() => setSuccess('')} style={styles.closeAlert}>×</button>
        </div>
      )}

      {/* Merchants Table */}
      <div style={styles.card}>
        <h2 style={styles.sectionTitle}>All Merchants</h2>

        {merchants.length === 0 ? (
          <div style={styles.noData}>
            <p>No merchants found. Click "Add Merchant" to create one.</p>
          </div>
        ) : (
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead style={styles.tableHeader}>
                <tr>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Code</th>
                  <th style={styles.th}>Receiving Account</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Transactions</th>
                  <th style={styles.th}>Volume</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {merchants.map((merchant) => (
                  <tr key={merchant.id} style={styles.tableRow}>
                    <td style={styles.td}>
                      <div style={styles.merchantName}>
                        {merchant.logoUrl && (
                          <img src={merchant.logoUrl} alt="" style={styles.logo} />
                        )}
                        <div>
                          <strong>{merchant.name}</strong>
                          {merchant.description && (
                            <div style={styles.description}>
                              {merchant.description.substring(0, 50)}...
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.codeTag}>{merchant.merchantCode}</span>
                    </td>
                    <td style={styles.td}>{merchant.receivingAccountNumber}</td>
                    <td style={styles.td}>
                      <span style={merchant.isActive ? styles.activeBadge : styles.inactiveBadge}>
                        {merchant.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={styles.td}>{merchant.totalTransactions?.toLocaleString() || 0}</td>
                    <td style={styles.td}>{formatCurrency(merchant.totalVolume)}</td>
                    <td style={styles.td}>
                      <div style={styles.actionButtons}>
                        <button
                          onClick={() => openViewModal(merchant)}
                          style={styles.viewButton}
                          title="View Details"
                        >
                          👁️
                        </button>
                        <button
                          onClick={() => openEditModal(merchant)}
                          style={styles.editButton}
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleToggleStatus(merchant)}
                          style={merchant.isActive ? styles.deactivateButton : styles.activateButton}
                          title={merchant.isActive ? 'Deactivate' : 'Activate'}
                        >
                          {merchant.isActive ? '🔴' : '🟢'}
                        </button>
                        <button
                          onClick={() => handleDelete(merchant)}
                          style={styles.deleteButton}
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h2>Add New Merchant</h2>
              <button onClick={() => setShowCreateModal(false)} style={styles.modalClose}>×</button>
            </div>
            <form onSubmit={handleCreate}>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label>Merchant Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    style={styles.input}
                    placeholder="e.g., SelectPay"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>Merchant Code *</label>
                  <input
                    type="text"
                    name="merchantCode"
                    value={formData.merchantCode}
                    onChange={handleInputChange}
                    required
                    style={styles.input}
                    placeholder="e.g., SELECTPAY"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>Receiving Account Number *</label>
                  <input
                    type="text"
                    name="receivingAccountNumber"
                    value={formData.receivingAccountNumber}
                    onChange={handleInputChange}
                    required
                    style={styles.input}
                    placeholder="Uptech wallet account number"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>Contact Email</label>
                  <input
                    type="email"
                    name="contactEmail"
                    value={formData.contactEmail}
                    onChange={handleInputChange}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>Contact Phone</label>
                  <input
                    type="text"
                    name="contactPhone"
                    value={formData.contactPhone}
                    onChange={handleInputChange}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>Website URL</label>
                  <input
                    type="url"
                    name="websiteUrl"
                    value={formData.websiteUrl}
                    onChange={handleInputChange}
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  style={styles.textarea}
                  rows={3}
                />
              </div>

              <div style={styles.checkboxGroup}>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                  />
                  Active
                </label>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="allowTransfers"
                    checked={formData.allowTransfers}
                    onChange={handleInputChange}
                  />
                  Allow Transfers
                </label>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="allowPayments"
                    checked={formData.allowPayments}
                    onChange={handleInputChange}
                  />
                  Allow Payments
                </label>
              </div>

              <div style={styles.modalFooter}>
                <button type="button" onClick={() => setShowCreateModal(false)} style={styles.cancelButton}>
                  Cancel
                </button>
                <button type="submit" style={styles.submitButton}>
                  Create Merchant
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedMerchant && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h2>Edit Merchant</h2>
              <button onClick={() => setShowEditModal(false)} style={styles.modalClose}>×</button>
            </div>
            <form onSubmit={handleUpdate}>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label>Merchant Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>Merchant Code</label>
                  <input
                    type="text"
                    name="merchantCode"
                    value={formData.merchantCode}
                    disabled
                    style={{...styles.input, backgroundColor: '#f0f0f0'}}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>Receiving Account Number *</label>
                  <input
                    type="text"
                    name="receivingAccountNumber"
                    value={formData.receivingAccountNumber}
                    onChange={handleInputChange}
                    required
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>Contact Email</label>
                  <input
                    type="email"
                    name="contactEmail"
                    value={formData.contactEmail}
                    onChange={handleInputChange}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>Contact Phone</label>
                  <input
                    type="text"
                    name="contactPhone"
                    value={formData.contactPhone}
                    onChange={handleInputChange}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>Website URL</label>
                  <input
                    type="url"
                    name="websiteUrl"
                    value={formData.websiteUrl}
                    onChange={handleInputChange}
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  style={styles.textarea}
                  rows={3}
                />
              </div>

              <div style={styles.checkboxGroup}>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                  />
                  Active
                </label>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="allowTransfers"
                    checked={formData.allowTransfers}
                    onChange={handleInputChange}
                  />
                  Allow Transfers
                </label>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="allowPayments"
                    checked={formData.allowPayments}
                    onChange={handleInputChange}
                  />
                  Allow Payments
                </label>
              </div>

              <div style={styles.modalFooter}>
                <button type="button" onClick={() => setShowEditModal(false)} style={styles.cancelButton}>
                  Cancel
                </button>
                <button type="submit" style={styles.submitButton}>
                  Update Merchant
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedMerchant && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h2>Merchant Details</h2>
              <button onClick={() => setShowViewModal(false)} style={styles.modalClose}>×</button>
            </div>
            <div style={styles.detailsGrid}>
              <div style={styles.detailItem}>
                <label>Name</label>
                <p>{selectedMerchant.name}</p>
              </div>
              <div style={styles.detailItem}>
                <label>Code</label>
                <p><span style={styles.codeTag}>{selectedMerchant.merchantCode}</span></p>
              </div>
              <div style={styles.detailItem}>
                <label>Receiving Account</label>
                <p>{selectedMerchant.receivingAccountNumber}</p>
              </div>
              <div style={styles.detailItem}>
                <label>Account Holder</label>
                <p>{selectedMerchant.receivingAccountName || 'N/A'}</p>
              </div>
              <div style={styles.detailItem}>
                <label>Status</label>
                <p>
                  <span style={selectedMerchant.isActive ? styles.activeBadge : styles.inactiveBadge}>
                    {selectedMerchant.isActive ? 'Active' : 'Inactive'}
                  </span>
                </p>
              </div>
              <div style={styles.detailItem}>
                <label>Contact Email</label>
                <p>{selectedMerchant.contactEmail || 'N/A'}</p>
              </div>
              <div style={styles.detailItem}>
                <label>Contact Phone</label>
                <p>{selectedMerchant.contactPhone || 'N/A'}</p>
              </div>
              <div style={styles.detailItem}>
                <label>Website</label>
                <p>{selectedMerchant.websiteUrl || 'N/A'}</p>
              </div>
              <div style={styles.detailItem}>
                <label>Total Transactions</label>
                <p>{selectedMerchant.totalTransactions?.toLocaleString() || 0}</p>
              </div>
              <div style={styles.detailItem}>
                <label>Total Volume</label>
                <p>{formatCurrency(selectedMerchant.totalVolume)}</p>
              </div>
              <div style={styles.detailItem}>
                <label>Created At</label>
                <p>{new Date(selectedMerchant.createdAt).toLocaleString()}</p>
              </div>
              <div style={styles.detailItem}>
                <label>Last Transaction</label>
                <p>{selectedMerchant.lastTransactionAt ? new Date(selectedMerchant.lastTransactionAt).toLocaleString() : 'N/A'}</p>
              </div>
            </div>
            {selectedMerchant.description && (
              <div style={styles.detailItem}>
                <label>Description</label>
                <p>{selectedMerchant.description}</p>
              </div>
            )}
            <div style={styles.modalFooter}>
              <button onClick={() => setShowViewModal(false)} style={styles.cancelButton}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '20px',
  },
  loading: {
    textAlign: 'center',
    marginTop: '100px',
    fontSize: '18px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '10px',
  },
  headerButtons: {
    display: 'flex',
    gap: '10px',
  },
  backButton: {
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '600',
    border: '1px solid #ddd',
    borderRadius: '6px',
    cursor: 'pointer',
    backgroundColor: 'white',
    color: '#333',
  },
  createButton: {
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '600',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    backgroundColor: '#28a745',
    color: 'white',
  },
  errorAlert: {
    backgroundColor: '#fee',
    color: '#c33',
    padding: '15px',
    borderRadius: '6px',
    marginBottom: '20px',
    border: '1px solid #fcc',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  successAlert: {
    backgroundColor: '#d4edda',
    color: '#155724',
    padding: '15px',
    borderRadius: '6px',
    marginBottom: '20px',
    border: '1px solid #c3e6cb',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  closeAlert: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: 'inherit',
  },
  card: {
    backgroundColor: 'white',
    padding: '30px',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  sectionTitle: {
    fontSize: '22px',
    marginBottom: '20px',
    color: '#333',
  },
  noData: {
    textAlign: 'center',
    color: '#666',
    padding: '40px',
  },
  tableContainer: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeader: {
    backgroundColor: '#f8f9fa',
  },
  th: {
    padding: '12px',
    textAlign: 'left',
    borderBottom: '2px solid #dee2e6',
    fontWeight: '600',
    color: '#495057',
    whiteSpace: 'nowrap',
  },
  tableRow: {
    borderBottom: '1px solid #dee2e6',
  },
  td: {
    padding: '12px',
    verticalAlign: 'middle',
  },
  merchantName: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  logo: {
    width: '32px',
    height: '32px',
    borderRadius: '4px',
    objectFit: 'cover',
  },
  description: {
    fontSize: '12px',
    color: '#666',
  },
  codeTag: {
    display: 'inline-block',
    padding: '4px 8px',
    backgroundColor: '#e3f2fd',
    color: '#1976d2',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600',
  },
  activeBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    backgroundColor: '#28a745',
    color: 'white',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '500',
  },
  inactiveBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    backgroundColor: '#dc3545',
    color: 'white',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '500',
  },
  actionButtons: {
    display: 'flex',
    gap: '5px',
  },
  viewButton: {
    padding: '6px 10px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    backgroundColor: '#17a2b8',
    fontSize: '14px',
  },
  editButton: {
    padding: '6px 10px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    backgroundColor: '#ffc107',
    fontSize: '14px',
  },
  activateButton: {
    padding: '6px 10px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    backgroundColor: '#28a745',
    fontSize: '14px',
  },
  deactivateButton: {
    padding: '6px 10px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    backgroundColor: '#6c757d',
    fontSize: '14px',
  },
  deleteButton: {
    padding: '6px 10px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    backgroundColor: '#dc3545',
    fontSize: '14px',
  },
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: 'white',
    padding: '30px',
    borderRadius: '8px',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  modalClose: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#666',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '15px',
  },
  formGroup: {
    marginBottom: '15px',
  },
  input: {
    width: '100%',
    padding: '10px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    boxSizing: 'border-box',
    marginTop: '5px',
  },
  textarea: {
    width: '100%',
    padding: '10px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    boxSizing: 'border-box',
    marginTop: '5px',
    resize: 'vertical',
  },
  checkboxGroup: {
    display: 'flex',
    gap: '20px',
    marginBottom: '20px',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    cursor: 'pointer',
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginTop: '20px',
  },
  cancelButton: {
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '600',
    border: '1px solid #ddd',
    borderRadius: '6px',
    cursor: 'pointer',
    backgroundColor: 'white',
    color: '#333',
  },
  submitButton: {
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '600',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    backgroundColor: '#007bff',
    color: 'white',
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '15px',
  },
  detailItem: {
    marginBottom: '10px',
  },
};
